import type {
    CanalLeads, LeadsPagina, OrdenarPaginas, TopPaginas,
} from '@/types/leadsDashboard';
import { n, pct, usd, usd2 } from '../format';

/**
 * Bloco 5.6 — Ranking de páginas.
 *
 * Fichas, não tabela — de propósito, para não repetir a linguagem visual do
 * bloco de países logo acima.
 *
 * O CARD MUDA COM O CANAL, e isso é o ponto central deste componente.
 *
 * Os dois canais fazem coisas diferentes e não compartilham métrica:
 *
 *   FLOW      capta. O lead nasce aqui. Não tem base, não tem alcançável e não
 *             tem cobertura — esses três são estoque que só o broadcast
 *             consome. O que mede o flow é quantos entraram e quanto cada um
 *             rendeu.
 *   BROADCAST reativa. Nunca captou ninguém. O que mede é quanto da base
 *             alcançável ele conseguiu tocar, e quanto vazou por 551.
 *
 * A versão anterior mostrava as MESMAS oito métricas nos dois filtros, então
 * "Captados 332" aparecia igual em broadcast (onde a captação é zero por
 * definição) e "Perdidos 551" aparecia em flow (onde o lead toma 551 mas
 * continua ativo — medido em 24/09: 369 ativos contra 4 inativos). Seis dos
 * oito números eram idênticos entre os dois filtros, o que fazia o filtro
 * parecer quebrado.
 *
 * Com busca ativa, o limite de Top N é ignorado: é assim que se consulta uma
 * página que não está no topo. E se a página do filtro global ficar fora do
 * Top N, ela aparece FIXADA no topo exibindo a posição REAL no ranking, não a
 * posição na tela.
 */

interface DefOrdem {
    k: OrdenarPaginas;
    l: string;
    rot: string;
    cor: string;
    /** Canais em que essa ordenação significa alguma coisa. */
    canais: CanalLeads[];
}

const TODOS: CanalLeads[] = ['todos', 'flow', 'broadcast'];

const ORDENACOES: DefOrdem[] = [
    // Captação não existe em broadcast: ordenar por ela lá ordenaria as
    // páginas por um número que o canal não produziu.
    { k: 'captados', l: 'Captação', rot: 'captados', cor: 'var(--blue)', canais: ['todos', 'flow'] },
    { k: 'receita', l: 'Receita', rot: 'no período', cor: 'var(--amber)', canais: TODOS },
    // LTV e Alcançados têm como denominador o alcance, que é conceito de
    // broadcast — em flow o eixo é captado, não alcançado.
    { k: 'ltv', l: 'LTV', rot: 'por lead alcançado', cor: 'var(--amber)', canais: ['todos', 'broadcast'] },
    { k: 'alcancados', l: 'Alcançados', rot: 'no período', cor: 'var(--violet)', canais: ['todos', 'broadcast'] },
    { k: 'ctr', l: 'CTR', rot: 'cliques ÷ entregas', cor: 'var(--teal)', canais: TODOS },
    { k: 'cliques', l: 'Cliques', rot: 'no período', cor: 'var(--green)', canais: TODOS },
];

const CANAIS: { k: CanalLeads; l: string; papel: string }[] = [
    { k: 'todos', l: 'Flow + Broad', papel: 'visão geral' },
    { k: 'flow', l: 'Flow', papel: 'capta o lead' },
    { k: 'broadcast', l: 'Broadcast', papel: 'reativa a base' },
];

const TOPS: TopPaginas[] = [10, 20, 30];

const ordensDoCanal = (canal: CanalLeads) => ORDENACOES.filter(o => o.canais.includes(canal));

/** Ordenação válida para o canal; cai no primeiro item quando a atual não serve. */
function ordemValida(ordem: OrdenarPaginas, canal: CanalLeads): OrdenarPaginas {
    const permitidas = ordensDoCanal(canal);
    return permitidas.some(o => o.k === ordem) ? ordem : permitidas[0].k;
}

/** Hero de cada canal quando a ordenação não manda: o número que define o canal. */
const HERO_PADRAO: Record<CanalLeads, OrdenarPaginas> = {
    todos: 'captados',
    flow: 'captados',
    broadcast: 'alcancados',
};

/** Valor da métrica de ordenação, já formatado conforme a natureza dela. */
function valorOrdenado(p: LeadsPagina, ordem: OrdenarPaginas, moeda: string): string {
    switch (ordem) {
        case 'receita': return usd(p.receita, moeda);
        case 'ltv': return usd2(p.ltv_por_lead_alcancado, moeda);
        case 'ctr': return ctr(p);
        case 'alcancados': return n(p.alcancados_periodo);
        case 'cliques': return n(p.cliques);
        default: return n(p.captados);
    }
}

function valorBruto(p: LeadsPagina, ordem: OrdenarPaginas): number {
    switch (ordem) {
        case 'receita': return p.receita;
        case 'ltv': return p.ltv_por_lead_alcancado;
        case 'ctr': return p.ctr_pct;
        case 'alcancados': return p.alcancados_periodo;
        case 'cliques': return p.cliques;
        default: return p.captados;
    }
}

/**
 * CTR sem denominador é "—", nunca "0,00%".
 *
 * `entregas_flow` só existe a partir de 24/09; dias anteriores vêm zerados e
 * um 0,00% ali seria lido como "ninguém clicou" quando o certo é "não dá para
 * saber". Ver migrations/add_entregas_flow_leads_dash.sql.
 */
function ctr(p: LeadsPagina): string {
    return p.entregas > 0 ? pct(p.ctr_pct, 2) : '—';
}

/** Receita por lead captado — o LTV do flow, que não tem "alcançado". */
function porCaptado(p: LeadsPagina): number {
    return p.captados > 0 ? p.receita / p.captados : 0;
}

interface Celula {
    r: string;
    v: string;
    /** Marca de quem produziu o número, quando o canal exibido mistura os dois. */
    dono?: string;
    /** Classe da célula (destaque de fundo). */
    cls?: string;
    /** Classe do valor. */
    vcls?: string;
    cor?: string;
}

/** A malha de métricas do card, que é diferente em cada canal. */
function celulas(p: LeadsPagina, canal: CanalLeads, moeda: string): Celula[] {
    if (canal === 'flow') {
        return [
            { r: 'Captados', v: n(p.captados), cor: 'var(--blue)' },
            { r: 'Entregas', v: n(p.entregas) },
            { r: 'Cliques', v: n(p.cliques) },
            { r: 'CTR', v: ctr(p), cor: 'var(--teal)' },
        ];
    }
    if (canal === 'broadcast') {
        return [
            { r: 'Base', v: n(p.base_total) },
            { r: 'Ainda alcançáveis', v: n(p.alcancaveis), vcls: 'muted' },
            { r: 'Alcançados', v: n(p.alcancados_periodo), cls: 'alccell', cor: 'var(--violet)' },
            { r: 'Cobertura', v: pct(p.cobertura_pct, 0) },
            { r: 'Perdidos 551', v: `−${n(p.perdidos_551)}`, cls: 'neg' },
            { r: 'Entregas', v: n(p.entregas) },
            { r: 'Cliques', v: n(p.cliques) },
            { r: 'CTR', v: ctr(p), cor: 'var(--teal)' },
        ];
    }
    // Visão geral: os dois canais no mesmo card, cada número marcado com o
    // canal que o produziu — sem isso vira a malha ambígua de antes.
    return [
        { r: 'Captados', v: n(p.captados), dono: 'flow', cor: 'var(--blue)' },
        { r: 'Perdidos 551', v: `−${n(p.perdidos_551)}`, dono: 'broad', cls: 'neg' },
        { r: 'Base', v: n(p.base_total) },
        { r: 'Ainda alcançáveis', v: n(p.alcancaveis), vcls: 'muted' },
        { r: 'Alcançados', v: n(p.alcancados_periodo), cls: 'alccell', cor: 'var(--violet)' },
        { r: 'Cliques', v: n(p.cliques) },
        { r: 'CTR', v: ctr(p), cor: 'var(--teal)' },
        { r: 'LTV / alcançado', v: usd2(p.ltv_por_lead_alcancado, moeda), cls: 'ltvcell' },
    ];
}

/** Linha de fechamento: dinheiro e a unidade econômica do canal. */
function rodape(p: LeadsPagina, canal: CanalLeads, moeda: string): string {
    const receita = usd(p.receita, moeda);
    if (canal === 'flow') {
        return `Receita ${receita} em flow · ${usd2(porCaptado(p), moeda)} por lead captado`;
    }
    if (canal === 'broadcast') {
        return `Receita ${receita} em broadcast · LTV ${usd2(p.ltv_por_lead_alcancado, moeda)} por alcançado`;
    }
    // Denominador NOMEADO: a malha acima mostra "Ainda alcançáveis" (estoque de
    // agora) e a cobertura usa o universo do período. Sem dizer qual é qual, os
    // dois números lado a lado pareciam não fechar.
    return `Receita ${receita} nos dois canais · alcançou ${pct(p.cobertura_pct, 0)} dos ${n(p.alcancaveis_no_periodo)} alcançáveis no período`;
}

interface Props {
    itens: LeadsPagina[];
    fixada: LeadsPagina | null;
    paginaSelecionada: string | null;
    ordenarPor: OrdenarPaginas;
    canal: CanalLeads;
    top: TopPaginas;
    busca: string;
    rotulo: string;
    nomePais?: string;
    moeda: string;
    carregando: boolean;
    onOrdenar: (o: OrdenarPaginas) => void;
    onCanal: (c: CanalLeads) => void;
    onTop: (t: TopPaginas) => void;
    onBusca: (b: string) => void;
    onPagina: (id: string | null) => void;
}

export default function RankingPaginas({
    itens, fixada, paginaSelecionada, ordenarPor, canal, top, busca, rotulo,
    nomePais, moeda, carregando, onOrdenar, onCanal, onTop, onBusca, onPagina,
}: Props) {
    const ordens = ordensDoCanal(canal);
    const def = ordens.find(o => o.k === ordenarPor)
        ?? ordens.find(o => o.k === HERO_PADRAO[canal])
        ?? ordens[0];
    const papel = CANAIS.find(c => c.k === canal)?.papel ?? '';
    const canalLabel = canal === 'flow' ? 'flow' : canal === 'broadcast' ? 'broadcast' : 'flow + broadcast';

    // Trocar de canal pode invalidar a ordenação atual (Captação em broadcast,
    // LTV em flow). Corrige junto com a troca: React agrupa os dois setState
    // do mesmo handler, então sai uma requisição só.
    const trocarCanal = (c: CanalLeads) => {
        const destino = ordemValida(ordenarPor, c);
        if (destino !== ordenarPor) onOrdenar(destino);
        onCanal(c);
    };

    // A fixada entra no topo da lista, mas mantendo a posição real dela.
    const lista = fixada ? [fixada, ...itens] : itens;
    const maxValor = Math.max(...lista.map(p => valorBruto(p, def.k)), 1e-6);

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Ranking de páginas</div>
                    <div className="card-s">
                        {busca
                            ? `${itens.length} resultado(s) para "${busca}"`
                            : `Top ${top} por ${def.l.toLowerCase()}`}
                        {nomePais && ` · ${nomePais}`} · {canalLabel} · {rotulo}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                        type="search" value={busca}
                        onChange={e => onBusca(e.target.value)}
                        placeholder="Buscar página ou país…"
                        aria-label="Buscar página"
                    />
                    <div className="seg">
                        {CANAIS.map(c => (
                            <button
                                key={c.k} type="button"
                                data-on={canal === c.k ? 1 : 0}
                                onClick={() => trocarCanal(c.k)}
                                title={c.papel}
                            >
                                {c.l}
                            </button>
                        ))}
                    </div>
                    <div className="seg">
                        {TOPS.map(t => (
                            <button
                                key={t} type="button"
                                data-on={top === t ? 1 : 0}
                                onClick={() => onTop(t)}
                                disabled={!!busca}
                            >
                                Top {t}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bar" style={{ margin: 0, border: 0, borderBottom: '1px solid var(--line2)', borderRadius: 0 }}>
                <span className="bar-l">Ordenar por</span>
                <div className="seg">
                    {ordens.map(o => (
                        <button
                            key={o.k} type="button"
                            data-on={def.k === o.k ? 1 : 0}
                            onClick={() => onOrdenar(o.k)}
                        >
                            {o.l}
                        </button>
                    ))}
                </div>
                {papel && <span className="pg-papel">{canal === 'todos' ? 'flow capta · broadcast reativa' : papel}</span>}
                {busca && (
                    <button type="button" className="clearall" onClick={() => onBusca('')}>
                        Limpar busca
                    </button>
                )}
            </div>

            {carregando ? (
                <div className="pgrid">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="skel skel-card" style={{ height: 150 }} />
                    ))}
                </div>
            ) : lista.length === 0 ? (
                // §8: sem resultado na busca tem mensagem direta com saída,
                // nunca área em branco.
                <div className="empty">
                    Nenhuma página com esse nome. Tente outro termo ou{' '}
                    <button type="button" className="clearall" onClick={() => onBusca('')}>
                        limpe a busca
                    </button>.
                </div>
            ) : (
                <div className="pgrid">
                    {lista.map(p => {
                        const ehFixada = fixada?.id === p.id;
                        const posicao = p.posicao_real ?? p.posicao;
                        const malha = celulas(p, canal, moeda);
                        return (
                            <button
                                key={`${p.id}-${ehFixada ? 'fix' : 'top'}`}
                                type="button"
                                className="pg"
                                data-sel={paginaSelecionada === p.id ? 1 : 0}
                                data-pin={ehFixada ? 1 : 0}
                                onClick={() => onPagina(paginaSelecionada === p.id ? null : p.id)}
                            >
                                <div className="pg-rank">{String(posicao).padStart(2, '0')}</div>
                                <div>
                                    <div className="pg-name">
                                        <span className="code">{p.pais}</span>{p.nome}
                                        {!p.ativa && <span className="pausada">pausada</span>}
                                    </div>
                                    <div className="pg-hero">
                                        <b className="num" style={{ color: def.cor }}>
                                            {valorOrdenado(p, def.k, moeda)}
                                        </b>
                                        <span>{def.rot}</span>
                                    </div>
                                    <div className="pg-track">
                                        <i style={{
                                            width: `${(valorBruto(p, def.k) / maxValor) * 100}%`,
                                            background: def.cor,
                                        }} />
                                    </div>
                                    <div className="pg-stats">
                                        {malha.map(c => (
                                            <div key={c.r} className={c.cls}>
                                                <u>
                                                    {c.r}
                                                    {c.dono && <i className="dono">{c.dono}</i>}
                                                </u>
                                                <b className={c.vcls ? `num ${c.vcls}` : 'num'} style={c.cor ? { color: c.cor } : undefined}>
                                                    {c.v}
                                                </b>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="pg-foot">
                                        {rodape(p, canal, moeda)}
                                        {ehFixada && ' · fixada pelo filtro'}
                                    </div>
                                    {canal === 'broadcast' && p.perdidos_551 > 0 && (
                                        // O 551 do broadcast desativa o lead; o do flow não.
                                        // Só o broadcast aparece aqui justamente por isso.
                                        <div className="pg-nota">
                                            Perda conta só o 551 de broadcast, que desativa o lead.
                                            O flow também toma 551, mas lá o lead segue ativo e
                                            recebe o próximo disparo.
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
